import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Avatar,
  useTheme,
} from '@mui/material'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact'
import BlockIcon from '@mui/icons-material/Block'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism'
import { useHandleChangeRoute } from 'data/hooks'
import Footer from 'components/Layout/Footer'

const WHY_MYSTETHI = [
  {
    id: 'no-credits',
    icon: BlockIcon,
    title: 'No Credits. Nobody Meters You.',
    description:
      'Other platforms ration everything — credits, InMails, pay-to-promote tiers. We don\'t. Post jobs, message candidates, fill roles. No caps.',
  },
  {
    id: 'unlimited',
    icon: AllInclusiveIcon,
    title: 'Unlimited Jobs, Seats, Candidates.',
    description:
      'One flat annual rate covers your entire recruiting team. Unlimited job postings, unlimited candidate conversations, unlimited seats.',
  },
  {
    id: 'no-agencies',
    icon: PeopleOutlineIcon,
    title: 'No Third-Party Recruiters on the Platform.',
    description:
      'You\'re not competing against the agencies you hired. Every candidate on MyStethi is here to talk directly to you.',
  },
  {
    id: 'high-intent',
    icon: ConnectWithoutContactIcon,
    title: 'High-Intent Candidates by Design.',
    description:
      'Our users joined because they want a job. You\'re not chasing passive profiles or scraping databases — these are active job-seekers ready for their next role.',
  },
  {
    id: 'no-placement-fees',
    icon: WorkOutlineIcon,
    title: 'No Placement Fees. Ever.',
    description:
      'Agencies charge 20-30% per hire — that\'s $30-50K per physician. MyStethi is a flat annual rate. Hire one or hire twenty, the price doesn\'t change.',
  },
  {
    id: 'mission',
    icon: VolunteerActivismIcon,
    title: 'Mission, Not Margin.',
    description:
      'Founded by two physicians and two technologists. Bootstrapped. Priced fair on purpose. We built this because we care about how clinicians find work.',
  },
]

const HOW_IT_WORKS_STEPS = [
  {
    id: 'post',
    step: 'Step 1',
    icon: WorkOutlineIcon,
    title: 'Post Your Positions',
    description: 'Describe what you\'re looking for — specialty, location, practice setting. Unlimited postings, no pay-to-promote.',
  },
  {
    id: 'match',
    step: 'Step 2',
    icon: PeopleOutlineIcon,
    title: 'Reach High-Intent Candidates',
    description:
      'Every candidate on MyStethi joined to get hired. Your listings reach active job-seekers who already understand your role — not passive browsers.',
  },
  {
    id: 'connect',
    step: 'Step 3',
    icon: ConnectWithoutContactIcon,
    title: 'Connect Directly',
    description:
      'Reach out to candidates directly. No agency middlemen, no credit costs per message, no gates between you and your next hire.',
  },
]

const TESTIMONIALS = [
  {
    id: 1,
    quote: 'Getting in on the ground floor of MyStethi has been exciting. The site provides ',
    highlight: 'direct connection between hospital in-house recruiters and candidates seeking jobs',
    quoteEnd:
      ". I've only been a subscriber for a few months and recently interviewed and extended an offer to an excellent candidate. Aleksey has been prompt to respond to questions and is proactive with ideas to further improve the site.",
    name: 'Jody Long',
    title: 'Recruitment Supervisor',
    organization: 'Sarah Bush Lincoln Health Center',
    avatar: null,
    highlightKey: 'warning',
  },
  {
    id: 2,
    quote:
      'MyStethi has been a valuable partner in physician recruitment. Their customer service team is responsive and easy to work with, and ',
    highlight: 'the platform has enabled us to connect with providers we may have otherwise missed',
    quoteEnd:
      '. In a competitive and challenging recruitment environment, MyStethi offers an important way to broaden our reach.',
    name: 'Amy Knox',
    title: 'Senior Sourcing Strategist',
    organization: 'Medical University of South Carolina Health',
    avatar: null,
    highlightKey: 'success',
  },
  {
    id: 3,
    quote: "We've enjoyed using the platform. It's simple and streamlined and has delivered ",
    highlight: 'quality, actionable candidates that our company has interviewed and considering hiring',
    quoteEnd: '. It has been a pleasant surprise in our recruiting toolbox.',
    name: 'Jacob Stephenson',
    title: 'Senior Director, Provider Recruiting',
    organization: 'United Digestive',
    avatar: null,
    highlightKey: 'ai',
  },
]

function HeroSection({ theme, onRequestDemo }) {
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        py: { xs: 10, md: 16 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.light,
                fontWeight: 600,
                letterSpacing: '0.1em',
                fontSize: '0.9rem',
                display: 'block',
                mb: 2,
              }}
            >
              FOR HEALTH SYSTEMS AND IN-HOUSE RECRUITERS
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.15,
                color: theme.palette.common.white,
                mb: 3,
              }}
            >
              Physician recruiting,{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.primary.light }}
              >
                without the meter running.
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: '1.15rem',
                lineHeight: 1.7,
                mb: 5,
                maxWidth: '560px',
              }}
            >
              Every other platform rations — credits, InMails, placement fees, premium tiers. We don't. Unlimited everything, for a flat annual rate. Built by two practicing physicians. Priced fair on purpose.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onRequestDemo}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.common.white,
                fontWeight: 600,
                px: 5,
                py: 1.75,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: theme.palette.shadows.colored.primary,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: theme.palette.shadows.colored.primaryHover,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Request a Demo
            </Button>
          </Grid>

          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              {[
                { value: '20–30%', label: 'Typical agency placement fee' },
                { value: '$30–50K', label: 'Cost per physician via agency' },
                { value: 'Flat Rate', label: 'MyStethi. Unlimited hiring.' },
              ].map((stat) => (
                <Grid item xs={12} key={stat.label}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '16px',
                      p: 3,
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.common.white,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem' }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

function WhyMyStethiSection({ theme }) {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            What Makes Us Different
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[600],
              fontSize: '1.1rem',
              lineHeight: 1.6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Six things you won't find on any other physician recruiting platform.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {WHY_MYSTETHI.map((item) => {
            const Icon = item.icon
            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.palette.shadows.medium,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.primary.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.dark,
                        mb: 3,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                        mb: 1.5,
                        fontSize: '1.05rem',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.grey[700],
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>
    </Box>
  )
}

function HowItWorksSection({ theme }) {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.primary.dark,
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.9rem',
              display: 'block',
              mb: 2,
            }}
          >
            HOW IT WORKS
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
            }}
          >
            Three Steps to Your Next Hire
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {HOW_IT_WORKS_STEPS.map((step) => {
            const Icon = step.icon
            return (
              <Grid item xs={12} md={4} key={step.id}>
                <Card
                  sx={{
                    height: '100%',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '16px',
                    backgroundColor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.palette.shadows.medium,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: theme.palette.primary.dark,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        display: 'block',
                        mb: 2,
                      }}
                    >
                      {step.step}
                    </Typography>
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.primary.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.dark,
                        mb: 3,
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.grey[900],
                        mb: 1.5,
                        fontSize: '1.1rem',
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.grey[700],
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>
    </Box>
  )
}

function TestimonialsSection({ theme }) {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.grey[600],
              fontWeight: 600,
              fontSize: '0.9rem',
              letterSpacing: '0.1em',
              mb: 2,
              display: 'block',
            }}
          >
            TESTIMONIALS
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
            }}
          >
            Hear from Recruiting Teams Using MyStethi
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {TESTIMONIALS.map((testimonial) => (
            <Grid item xs={12} md={4} key={testimonial.id}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: { xs: 'auto', md: '400px' },
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: '3rem',
                      color: theme.palette.primary.main,
                      fontWeight: 'bold',
                      lineHeight: 1,
                      fontFamily: 'serif',
                    }}
                  >
                    &ldquo;
                  </Typography>
                </Box>

                <Box sx={{ mb: 4, flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.grey[900],
                      fontSize: '1rem',
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                    }}
                  >
                    {testimonial.quote}
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: theme.palette[testimonial.highlightKey].light,
                        color: theme.palette[testimonial.highlightKey].dark,
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '4px',
                        fontWeight: 600,
                        fontStyle: 'normal',
                      }}
                    >
                      {testimonial.highlight}
                    </Box>
                    {testimonial.quoteEnd}
                  </Typography>
                </Box>

                <Box sx={{ mt: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={testimonial.avatar}
                      sx={{
                        width: 48,
                        height: 48,
                        mr: 2,
                        backgroundColor: theme.palette.primary.light,
                      }}
                    >
                      {testimonial.name.split(' ').map((n) => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.grey[900],
                          mb: 0.5,
                          fontSize: '1rem',
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.grey[600],
                          mb: 0.25,
                          fontSize: '0.9rem',
                        }}
                      >
                        {testimonial.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.grey[500], fontSize: '0.85rem' }}
                      >
                        {testimonial.organization}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

function CtaSection({ theme, onRequestDemo }) {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.common.white,
            fontSize: { xs: '2rem', md: '2.75rem' },
            lineHeight: 1.2,
            mb: 3,
          }}
        >
          Stop Paying Per Hire. Start Hiring.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
            mb: 5,
            maxWidth: '540px',
            mx: 'auto',
          }}
        >
          One flat annual rate. Unlimited jobs, seats, and candidate conversations. No credits, no placement fees, no catch.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onRequestDemo}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            px: 6,
            py: 2,
            fontSize: '1.1rem',
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: theme.palette.shadows.colored.primary,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: theme.palette.shadows.colored.primaryHover,
              transform: 'translateY(-2px)',
            },
          }}
        >
          Request a Demo
        </Button>
      </Container>
    </Box>
  )
}

const RecruiterLandingPage = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  const handleRequestDemo = () => {
    handleChangeRoute('/demo')()
  }

  useEffect(() => {
    document.title = 'Physician Recruiting Without the Meter Running | MyStethi'

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.name = 'description'
      document.head.appendChild(metaDescription)
    }
    metaDescription.content = 'Physician recruiting without the meter running. No credits, no caps, no placement fees — unlimited jobs, seats, and direct candidate conversations for a flat annual rate. Built by practicing physicians.'

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "MyStethi for Health Systems",
      "description": "Physician recruiting without the meter running. Unlimited jobs, seats, and direct candidate conversations for a flat annual rate.",
      "url": "https://www.mystethi.com/recruiters",
      "publisher": {
        "@type": "Organization",
        "name": "MyStethi",
        "url": "https://www.mystethi.com"
      }
    }

    let structuredDataScript = document.getElementById('recruiters-structured-data')
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.id = 'recruiters-structured-data'
      structuredDataScript.type = 'application/ld+json'
      document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(structuredData)

    return () => {
      const script = document.getElementById('recruiters-structured-data')
      if (script) {
        document.head.removeChild(script)
      }
    }
  }, [])

  return (
    <Box component="main" sx={{ width: '100%' }}>
      <HeroSection theme={theme} onRequestDemo={handleRequestDemo} />
      <WhyMyStethiSection theme={theme} />
      <HowItWorksSection theme={theme} />
      <TestimonialsSection theme={theme} />
      <CtaSection theme={theme} onRequestDemo={handleRequestDemo} />
      <Footer />
    </Box>
  )
}

export default RecruiterLandingPage
